import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DOCUMENT_KINDS,
  type DocumentDto,
  type DocumentKind,
  type DocumentStatus,
} from '@shared';
import { apiGet, apiPost, ApiError } from '../../lib/api';

// Reports & deliverables — staff view of every document on the platform plus
// the dual-sign-off queue for final reports (REQ-011).
//
// Storage is metadata-only in v1; S3 wiring is queued for Phase 5. The
// "Upload" form here records the document row with a placeholder storage URL
// so the dual-sign-off pipeline can be exercised end-to-end ahead of S3.

const KIND_LABEL: Record<DocumentKind, string> = {
  kyc: 'KYC pack',
  deliverable: 'Deliverable',
  final_report: 'Final report',
  engagement_letter: 'Engagement letter',
  other: 'Other',
};

const STATUS_LABEL: Record<DocumentStatus, string> = {
  uploaded: 'Uploaded',
  pending_lead_signoff: 'Awaiting lead sign-off',
  pending_compliance_signoff: 'Awaiting compliance sign-off',
  approved: 'Approved',
  archived: 'Archived',
};

const STATUS_TONE: Record<DocumentStatus, string> = {
  uploaded: 'neutral',
  pending_lead_signoff: 'amber',
  pending_compliance_signoff: 'amber',
  approved: 'green',
  archived: '',
};

type Filter = 'all' | 'pending_signoff' | DocumentKind;

interface UploadForm {
  kind: DocumentKind;
  title: string;
  request_id: string;
  client_id: string;
  notes: string;
  // File picker state — populated when the operator selects a file. Filename
  // and size flow into the metadata POST after the PUT to Azure succeeds.
  file: File | null;
}

const emptyUpload: UploadForm = {
  kind: 'final_report',
  title: '',
  request_id: '',
  client_id: '',
  notes: '',
  file: null,
};

export function AdminReports() {
  const [rows, setRows] = useState<DocumentDto[] | null>(null);
  const [filter, setFilter] = useState<Filter>('pending_signoff');
  const [error, setError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [up, setUp] = useState<UploadForm>(emptyUpload);
  const [busy, setBusy] = useState<string | null>(null);
  // Inline confirmation after a successful upload. Mirrors the green banner
  // pattern from TriageDrawer / ClientEditDrawer / InviteContactModal so the
  // user gets the same recognisable success signal everywhere.
  const [justUploaded, setJustUploaded] = useState<null | { kind: DocumentKind; title: string }>(null);

  async function load() {
    try {
      const r = await apiGet<{ documents: DocumentDto[] }>('/api/documents');
      setRows(r.documents);
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Could not load documents.');
    }
  }

  useEffect(() => { void load(); }, []);

  const counts = useMemo(() => {
    let pending = 0, approved = 0, total = (rows ?? []).length;
    for (const r of rows ?? []) {
      if (r.status === 'pending_lead_signoff' || r.status === 'pending_compliance_signoff') pending++;
      if (r.status === 'approved') approved++;
    }
    return { pending, approved, total };
  }, [rows]);

  const list = useMemo(() => {
    const all = rows ?? [];
    if (filter === 'all') return all;
    if (filter === 'pending_signoff') {
      return all.filter((r) => r.status === 'pending_lead_signoff' || r.status === 'pending_compliance_signoff');
    }
    return all.filter((r) => r.kind === filter);
  }, [rows, filter]);

  // Three-step upload to Azure Blob Storage (Phase F):
  //   1. POST /api/documents/upload-url → server mints opaque blob_name + SAS PUT URL
  //   2. PUT the file directly to Azure (no transit through our API)
  //   3. POST /api/documents to register the metadata row, with storage_url=blob_name
  //
  // Errors surface inline. On success, form resets + list reloads so the new
  // row appears in the dual-sign-off queue (final_report) or "uploaded" tab.
  async function handleUpload() {
    if (!up.title.trim() || !up.file) {
      setError('Title and a file are required.');
      return;
    }
    if (!up.request_id.trim() && !up.client_id.trim()) {
      setError('Either request_id or client_id must be provided.');
      return;
    }
    if (up.file.size > 50 * 1024 * 1024) {
      setError('File too large — max 50 MB.');
      return;
    }
    setBusy('upload');
    setError(null);
    try {
      // Step 1 — get the upload URL.
      const sas = await apiPost<{ upload_url: string; blob_name: string; expires_at: string }>(
        '/api/documents/upload-url',
        {
          filename: up.file.name,
          mime_type: up.file.type || 'application/octet-stream',
          size_bytes: up.file.size,
        },
      );

      // Step 2 — PUT the file directly to Azure. Azure requires the
      // x-ms-blob-type header for block blobs.
      const putRes = await fetch(sas.upload_url, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'content-type': up.file.type || 'application/octet-stream',
        },
        body: up.file,
      });
      if (!putRes.ok) {
        throw new Error(`Azure upload failed (HTTP ${putRes.status}). The link may have expired — try again.`);
      }

      // Step 3 — register the document metadata with the blob_name as storage_url.
      await apiPost('/api/documents', {
        kind: up.kind,
        title: up.title.trim(),
        filename: up.file.name,
        mime_type: up.file.type || 'application/octet-stream',
        size_bytes: up.file.size,
        storage_url: sas.blob_name,
        request_id: up.request_id.trim() || undefined,
        client_id: up.client_id.trim() || undefined,
        notes: up.notes.trim() || undefined,
      });

      // Show the confirmation inline, refresh the list in the background,
      // then auto-close after a beat so the user registers the success.
      setJustUploaded({ kind: up.kind, title: up.title.trim() });
      await load();
      setTimeout(() => {
        setUp(emptyUpload);
        setShowUpload(false);
        setJustUploaded(null);
      }, 1400);
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : (err as Error)?.message ?? 'Upload failed.');
    } finally {
      setBusy(null);
    }
  }

  // Download button — call /api/documents/:id/download to mint a fresh SAS
  // URL each click (5-minute TTL) then open it in a new tab. We don't 302
  // server-side because that would force the browser to send our session
  // cookie to Azure, which would 400. New tab matches the client portal's
  // /portal/documents behaviour so the admin shell stays put for the
  // operator (their place in the list, filter, scroll position).
  async function handleDownload(d: DocumentDto) {
    setBusy(`download-${d.id}`);
    setError(null);
    try {
      const r = await apiGet<{ url: string }>(`/api/documents/${d.id}/download`);
      window.open(r.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Download failed.');
    } finally {
      setBusy(null);
    }
  }

  async function handleSignoff(d: DocumentDto, who: 'lead' | 'compliance') {
    setBusy(`signoff-${d.id}`);
    setError(null);
    try {
      await apiPost(`/api/documents/${d.id}/signoff`, { who });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Sign-off failed.');
    } finally {
      setBusy(null);
    }
  }

  async function handlePublish(d: DocumentDto) {
    setBusy(`publish-${d.id}`);
    setError(null);
    try {
      await apiPost(`/api/documents/${d.id}/publish`, {});
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Publish failed.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div>
        <h1 className="portal-h1">Reports &amp; deliverables</h1>
        <p className="portal-sub">
          Documents on the platform plus the dual-sign-off queue for final reports (REQ-011).
          Files upload directly to Azure Blob Storage; downloads use short-lived signed URLs.
        </p>
      </div>

      <div className="portal-cards">
        <div className="card">
          <div className="lbl">Pending sign-off</div>
          <div className="val">{counts.pending}</div>
          <div className="delta">Final reports awaiting Lead or Compliance</div>
        </div>
        <div className="card">
          <div className="lbl">Approved</div>
          <div className="val">{counts.approved}</div>
          <div className="delta">Ready to publish</div>
        </div>
        <div className="card">
          <div className="lbl">Total documents</div>
          <div className="val">{counts.total}</div>
          <div className="delta">All kinds</div>
        </div>
        <div className="card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <button type="button" className="btn" onClick={() => setShowUpload(!showUpload)}>
            {showUpload ? 'Close upload' : '+ Upload document'}
          </button>
          <div className="delta" style={{ marginTop: 6 }}>Direct to Azure · 50 MB max</div>
        </div>
      </div>

      {showUpload && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3 className="proposal-section-h">Upload document</h3>
          <div className="prop-grid">
            <div>
              <label className="prop-lbl" htmlFor="up-kind">Kind</label>
              <select
                id="up-kind"
                className="prop-input"
                value={up.kind}
                disabled={busy !== null}
                onChange={(e) => setUp((s) => ({ ...s, kind: e.target.value as DocumentKind }))}
              >
                {DOCUMENT_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
              </select>
            </div>
            <div>
              <label className="prop-lbl" htmlFor="up-title">Title</label>
              <input
                id="up-title"
                className="prop-input"
                value={up.title}
                disabled={busy !== null}
                onChange={(e) => setUp((s) => ({ ...s, title: e.target.value }))}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="prop-lbl" htmlFor="up-file">File</label>
              <input
                id="up-file"
                className="prop-input"
                type="file"
                disabled={busy !== null}
                onChange={(e) => setUp((s) => ({ ...s, file: e.target.files?.[0] ?? null }))}
              />
              {up.file && (
                <div style={{ fontSize: 12, color: 'var(--fg-mute)', marginTop: 6 }}>
                  {up.file.name} · {(up.file.size / 1024 / 1024).toFixed(2)} MB · {up.file.type || 'unknown type'}
                </div>
              )}
            </div>
            <div style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--fg-mute)', marginTop: 4 }}>
              Scope — provide <strong style={{ color: 'var(--fg)' }}>at least one</strong> of the two below.
              Use Brief id to attach the document to a single engagement, or Client id to attach it to a client as a whole.
            </div>
            <div>
              <label className="prop-lbl" htmlFor="up-req">Brief id</label>
              <input
                id="up-req"
                className="prop-input"
                placeholder="OMG-1234"
                value={up.request_id}
                disabled={busy !== null}
                onChange={(e) => setUp((s) => ({ ...s, request_id: e.target.value }))}
              />
              <div style={{ fontSize: 11, color: 'var(--fg-mute)', marginTop: 4 }}>
                e.g. <span style={{ fontFamily: 'var(--mono)' }}>OMG-3201</span>
              </div>
            </div>
            <div>
              <label className="prop-lbl" htmlFor="up-client">Client id</label>
              <input
                id="up-client"
                className="prop-input"
                placeholder="c-001"
                value={up.client_id}
                disabled={busy !== null}
                onChange={(e) => setUp((s) => ({ ...s, client_id: e.target.value }))}
              />
              <div style={{ fontSize: 11, color: 'var(--fg-mute)', marginTop: 4 }}>
                e.g. <span style={{ fontFamily: 'var(--mono)' }}>c-001</span>
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="prop-lbl" htmlFor="up-notes">Notes (optional)</label>
              <textarea
                id="up-notes"
                rows={2}
                className="prop-textarea"
                value={up.notes}
                disabled={busy !== null}
                onChange={(e) => setUp((s) => ({ ...s, notes: e.target.value }))}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <button type="button" className="btn" onClick={handleUpload} disabled={busy === 'upload' || justUploaded !== null}>
                {busy === 'upload' ? 'Uploading…' : justUploaded ? 'Uploaded ✓' : 'Upload document'}
              </button>
              {error && !justUploaded && (
                <div
                  role="alert"
                  style={{
                    marginTop: 12,
                    padding: '10px 14px',
                    borderRadius: 'var(--r-sm)',
                    background: 'color-mix(in oklab, var(--red, #c0392b) 8%, transparent)',
                    color: 'var(--red, #c0392b)',
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  {error}
                </div>
              )}
              {justUploaded && (
                <div
                  role="status"
                  aria-live="polite"
                  style={{
                    marginTop: 12,
                    padding: '10px 14px',
                    borderRadius: 'var(--r-sm)',
                    background: 'color-mix(in oklab, var(--green, #1f8a4c) 12%, transparent)',
                    color: 'var(--green, #1f8a4c)',
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  <div style={{ fontWeight: 500 }}>
                    <span aria-hidden="true">✓</span> Document uploaded — “{justUploaded.title}”
                  </div>
                  <div style={{ marginTop: 4, color: 'var(--fg-soft)', fontSize: 12 }}>
                    {justUploaded.kind === 'final_report'
                      ? 'It now sits in the Pending sign-off queue. The engagement lead must sign before compliance can countersign.'
                      : 'It’s now visible in the list below under its kind tab.'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="tabs" style={{ marginTop: 24 }}>
        <button className={filter === 'pending_signoff' ? 'on' : ''} onClick={() => setFilter('pending_signoff')}>
          Pending sign-off {counts.pending > 0 && <span className="tab-count">{counts.pending}</span>}
        </button>
        <button className={filter === 'all' ? 'on' : ''} onClick={() => setFilter('all')}>All</button>
        {DOCUMENT_KINDS.map((k) => (
          <button key={k} className={filter === k ? 'on' : ''} onClick={() => setFilter(k)}>
            {KIND_LABEL[k]}
          </button>
        ))}
      </div>

      {error && (
        <div className="card" style={{ marginTop: 16, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      <div className="card flush">
        <table className="tbl">
          <thead>
            <tr>
              <th>Title</th>
              <th>Kind</th>
              <th>Scope</th>
              <th>Status</th>
              <th>Visibility</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr><td colSpan={6} style={{ color: 'var(--fg-mute)', fontSize: 13 }}>Loading…</td></tr>
            )}
            {rows !== null && list.length === 0 && (
              <tr>
                <td colSpan={6} style={{ color: 'var(--fg-mute)', fontSize: 13, textAlign: 'center', padding: 32 }}>
                  No documents in this view.
                </td>
              </tr>
            )}
            {list.map((d) => (
              <tr key={d.id}>
                <td>
                  <div style={{ fontWeight: 500 }}>{d.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>{d.filename}</div>
                </td>
                <td style={{ color: 'var(--fg-soft)', fontSize: 13 }}>{KIND_LABEL[d.kind]}</td>
                <td style={{ color: 'var(--fg-soft)', fontSize: 12, fontFamily: 'var(--mono)' }}>
                  {d.request_id ?? d.client_id ?? '—'}
                </td>
                <td>
                  <span className={`chip ${STATUS_TONE[d.status]}`}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
                    {STATUS_LABEL[d.status]}
                  </span>
                </td>
                <td style={{ fontSize: 12, color: d.visible_to_client ? 'var(--accent)' : 'var(--fg-mute)' }}>
                  {d.visible_to_client ? 'Visible to client' : 'Staff only'}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button type="button" className="btn ghost sm" disabled={busy === `download-${d.id}`} onClick={() => handleDownload(d)}>
                      {busy === `download-${d.id}` ? 'Opening…' : 'Download'}
                    </button>
                    {d.kind === 'final_report' && !d.lead_signed_at && (
                      <button type="button" className="btn ghost sm" disabled={busy === `signoff-${d.id}`} onClick={() => handleSignoff(d, 'lead')}>
                        Lead sign-off
                      </button>
                    )}
                    {d.kind === 'final_report' && !d.compliance_signed_at && (
                      <button type="button" className="btn ghost sm" disabled={busy === `signoff-${d.id}`} onClick={() => handleSignoff(d, 'compliance')}>
                        Compliance sign-off
                      </button>
                    )}
                    {(d.kind !== 'final_report' || d.status === 'approved') && !d.visible_to_client && (
                      <button type="button" className="btn sm" disabled={busy === `publish-${d.id}`} onClick={() => handlePublish(d)}>
                        Publish
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
